// 공통 함수: apiVersion 변경 시 해당 캐시 초기화
import _ from 'lodash'
import axios from 'axios'
import CryptoJS from 'crypto-js'
import memoryDriver from 'localforage-memoryStorageDriver'
import localforage from 'localforage'
import Vue from 'vue'
import { getApiBaseUrl } from '~/utils'

let onlyFirst = true

function clearGnbMenuCache (store) {
  localStorage.removeItem('local_store')
  store.commit('local/clearGnbMenuList')
}

async function handleCache (apiCache, apiVersionFromLocalStorage, apiVersionFromS3, cacheType) {
  if (_.isEmpty(apiVersionFromLocalStorage)) {
    return true
  } else if (!_.isEqual(apiVersionFromLocalStorage, apiVersionFromS3.toString())) {
    // api가 변경되었을 때
    console.log(`${cacheType} apiVersion이 변경되었습니다. ${cacheType}를 초기화합니다.`)
    return await clearCache(apiCache, cacheType) // 공통의 clearCache 함수 호출
  }
  return true
}

// 공통 캐시 초기화 함수
async function clearCache (cache, cacheType) {
  try {
    const keys = await cache.keys()
    if (keys.length > 0) {
      // 저장소에 데이터가 있으면, 모든 데이터를 삭제
      console.log(`${cacheType}에 데이터가 있습니다. 모든 데이터를 삭제합니다.`)
      await cache.clear()
      console.log(`${cacheType}에 저장된 모든 데이터를 삭제했습니다.`)
    } else {
      // 저장소가 비어 있는 경우
      console.log(`${cacheType}는 비어 있습니다.`)
    }
    return true
  } catch (error) {
    console.error(`${cacheType} 저장소 확인 중 오류 발생:`, error)
    return false
  }
}

// 캐시 처리 함수 (postCache, ssnCache, homeCache 모두 통합)
async function handleCacheForAll (cache, apiVersionFromLocalStorage, apiVersionFromS3, cacheType) {
  return await handleCache(cache, apiVersionFromLocalStorage, apiVersionFromS3, cacheType)
}

// 초기화로직 (최초진입시 비교, 페이지 이동시 비교)
async function firstOrRouteChange (store) {
  // pjm onlyFirst false일때 동작하는 캐시초기화 로직
  const uniqueKey = Date.now()
  const apiVersionFromLocalStorage = localStorage.getItem('apiVersion')
  const s3Url = process.env.S3_API_CACHE_URL + '/apiVersion.txt?v=' + uniqueKey
  const apiVersionFromS3 = await axios.get(s3Url, { timeout: 2000 }).then((res) => {
    return res.data
  }).catch((e) => {
    console.log(e)
  })
  if (apiVersionFromS3 && !_.isEqual(apiVersionFromLocalStorage, apiVersionFromS3.toString())) {
    // 다르면 초기화
    // 페이지이동시 캐시초기화
    clearGnbMenuCache(store)
    localforage.defineDriver(memoryDriver)

    // 'ssnCache'가 'daiso-cache'라는 name 아래에 존재하는지 확인
    const ssnCacheItem = localforage.createInstance({
      driver: [localforage.INDEXEDDB],
      name: 'daiso-cache', // 데이터베이스 이름
      storeName: 'ssnCache' // 실제 저장소 이름
    })

    // 'postCache'도 함께 초기화 (AxiosBuilderPostCache용)
    const postCacheItem = localforage.createInstance({
      driver: [localforage.INDEXEDDB],
      name: 'daiso-cache', // 데이터베이스 이름
      storeName: 'postCache' // 실제 저장소 이름
    })
    const cacheResults = await Promise.all([
      handleCacheForAll(ssnCacheItem, apiVersionFromLocalStorage, apiVersionFromS3, 'ssnCache'),
      handleCacheForAll(postCacheItem, apiVersionFromLocalStorage, apiVersionFromS3, 'postCache')
    ])

    if (cacheResults.every(Boolean)) {
      localStorage.setItem('apiVersion', apiVersionFromS3)
    }
  }
}

export default async ({ app, $axios, store }) => {
  // popstate 이벤트를 직접 감지 (window.event는 async 함수 내에서 유실될 수 있음)
  let isPopstateNavigation = false
  window.addEventListener('popstate', () => {
    isPopstateNavigation = true
  })

  await app.router.beforeEach(async (to, from, next) => {
    // popstate 플래그를 즉시 읽고 리셋
    const isPopstate = isPopstateNavigation
    isPopstateNavigation = false

    if (isPopstate) {
      store.commit('page/setPopstate', true)
    } else {
      store.commit('page/setPopstate', false)
    }

    try {
      window.groobee?.closeCampaign()
    } catch (e) {
      console.log(e)
    }

    // 고객 Id가 있는 경우 GTM DataLayer Push
    window.dataLayer = window.dataLayer || []

    window.history.scrollRestoration = 'manual'

    const userId = store.getters['session/getMb']?.mbEid ?? ''

    if (userId) {
      window.dataLayer.push({
        userId
      })
    }
    if (from?.path !== '/' && to?.path === '/ds') {
      Vue.prototype.$airbridge.goHome()
    }

    const doNext = true
    console.log('route.js')

    if (onlyFirst) {
      onlyFirst = false
      if (typeof window !== 'undefined') {
        if (performance.getEntriesByType('navigation')[0].type === 'reload') {
          clearGnbMenuCache(store)
          const ssnCacheItem = localforage.createInstance({
            driver: [localforage.INDEXEDDB],
            name: 'daiso-cache', // 데이터베이스 이름
            storeName: 'ssnCache' // 실제 저장소 이름
          })

          // postCache도 함께 초기화
          const postCacheItem = localforage.createInstance({
            driver: [localforage.INDEXEDDB],
            name: 'daiso-cache', // 데이터베이스 이름
            storeName: 'postCache' // 실제 저장소 이름
          })
          await Promise.all([
            clearCache(ssnCacheItem, 'ssnCache'),
            clearCache(postCacheItem, 'postCache')
          ])
        } else {
          await firstOrRouteChange(store).catch((err) => {
            console.error(err)
          })
        }
      }
    } else {
      // pjm onlyFirst false일때 동작하는 캐시초기화 로직
      await firstOrRouteChange(store).catch((err) => {
        console.error(err)
      })
    }

    try {
      let isLogging = false
      const mb = store.getters['session/getMb']
      if (mb.mbMngNo !== undefined && mb.mbMngNo !== '') {
        const date = new Date(Date.now())
        const lastVisitedStr = localStorage.getItem('last_visited')
        if (lastVisitedStr) {
          const lastVisited = JSON.parse(lastVisitedStr)
          const encryptedDataWithIv = lastVisited.mbMngNo // 저장된 암호문 (IV + 암호문)
          const key = CryptoJS.enc.Utf8.parse(process.env.ENCRYPTION_KEY)
          const ivBase64 = encryptedDataWithIv.slice(0, 24)
          const encrypted = encryptedDataWithIv.slice(24)
          const iv = CryptoJS.enc.Base64.parse(ivBase64)
          const decrypted = CryptoJS.AES.decrypt(encrypted, key, { iv })
          const originalText = decrypted.toString(CryptoJS.enc.Utf8)
          if (originalText !== mb.mbMngNo) {
            isLogging = true
          } else if (Date.now() - lastVisited.time >= 60 * 60 * 1000) {
            isLogging = true
          } else {
            const lastVisitedTime = new Date(lastVisited.time)
            if (date.getFullYear() !== lastVisitedTime.getFullYear() || date.getMonth() !== lastVisitedTime.getMonth() || date.getDate() !== lastVisitedTime.getDate()) {
              isLogging = true
            }
          }
        } else {
          isLogging = true
        }
        const randomIV = CryptoJS.lib.WordArray.random(16)
        const encrypted = CryptoJS.AES.encrypt(mb.mbMngNo, CryptoJS.enc.Utf8.parse(process.env.ENCRYPTION_KEY), { iv: randomIV }).toString()
        const encryptedDataWithIv = randomIV.toString(CryptoJS.enc.Base64) + encrypted
        const saveLastVisited = {
          mbMngNo: encryptedDataWithIv,
          time: Date.now(),
        }
        localStorage.setItem('last_visited', JSON.stringify(saveLastVisited))
      } else {
        localStorage.removeItem('last_visited')
      }
      if (isLogging) {
        const agent = navigator.userAgent
        const isIphone = /iPhone/i.test(agent)
        const isAndroid = /Android/i.test(agent)
        // const isPC = isIphone === false && isAndroid === false ? /Windows|Macintosh|Linux/i.test(agent) : false
        const isApp = /APP_DAISO/i.test(agent)
        $axios.$post(getApiBaseUrl() + '/mb/mbb/insertAutoLoginLog', {
          userNo: mb.mbMngNo,
          deviceType: isAndroid ? '2' : isIphone ? '3' : '1',
          isApp: isApp ? '1' : '2',
        }).then((res) => {
          if (!res.success) {
            // console.log('visited logging err', res.error)
          }
        })
      }
    } catch (e) {
      // console.log('visited logging err', e)
    }

    // let isLogin = null
    // await $axios.$post(process.env.API_BASE_URL + '/mb/mbb/token')
    //   .then((res) => {
    //     isLogin = res.success
    //   })
    //   .catch(() => { isLogin = false })
    //
    // if (!isLogin) {
    //   // 세션삭제
    //   store.commit('session/setMember', {})
    // } else {
    //   await $axios.$post(process.env.API_BASE_URL + '/mb/mbb/getMbInfoByToken')
    //     .then((res) => {
    //       if (res.success) {
    //         store.commit('session/setMember', res.data)
    //       }
    //     })
    // }

    if (doNext) {
      // 결제완료/결제실패 화면에서 뒤로가기 버튼 클릭시 장바구니 이동
      if (['/', '/or/ora/SCR_ORA_0006'].includes(from.path) && ['/or/ora/SCR_ORA_0001', '/or/ora/SCR_ORA_0007', '/fb/fba/SCR_FBA_0006', '/bp/bpa/SCR_BPA_0008'].includes(to.path)) {
        const orBackPath = localStorage.getItem('orBackPath')
        // console.log('◀■■ beforeEach::orBackPath', orBackPath)
        if (orBackPath) {
          await next({ path: orBackPath })
        } else {
          await next()
        }
      } else if (from.path === '/bp/bpa/SCR_BPA_0008' && to.path === '/bp/bpa/SCR_BPA_0001') { // 대량주문 주문서 뒤로가기 버튼 클릭시 장바구니 이동
        await next({ path: '/or/orc/SCR_ORC_0001?tab=04' })
      } else if (from.path === '/or/ORC/SCR_ORC_0001' && to.path === '/or/ora/SCR_ORA_0006') { // 장바구니에서 주문완료/실패 화면 이동
        await next(false)
      } else {
        await next()
      }
    } else {
      await next(false)
    }
  })

  // for groobee
  app.router.afterEach(async (to, from) => {
    const accountInfo = store.getters['session/getAccountInfo']
    const collectTy = store.getters['session/getMb']?.collectTy
    const metaTags = [
      { hid: 'groobee:member_id', property: 'groobee:member_id', content: accountInfo?.mbMngNo ?? '' },
      { hid: 'groobee:member_grade', property: 'groobee:member_grade', content: '' },
      { hid: 'groobee:member_gender', property: 'groobee:member_gender', content: collectTy === 'A' ? accountInfo?.sexdSe ?? '' : '' },
      { hid: 'groobee:member_type', property: 'groobee:member_type', content: accountInfo?.userTy ?? '' },
      { hid: 'groobee:member_age', property: 'groobee:member_age', content: collectTy === 'A' ? accountInfo?.ageGroup ?? '' : '' }
    ]
    const currentMetaTags = app.head.meta
    metaTags.forEach((tag) => {
      const index = currentMetaTags.findIndex(existingTag => existingTag.hid === tag.hid)
      if (index !== -1) {
        currentMetaTags.splice(index, 1) // 기존 메타 태그 삭제
      }
    })
    app.head.meta = [...app.head.meta, ...metaTags]

    // collectTy가 'N' 또는 'P'인 경우에만 쿠키 제거 API 호출
    if (collectTy === 'N' || collectTy === 'P') {
      try {
        await $axios.get(process.env.WEB_BASE_URL + '/groobee/groobee-remove-cookies')
      } catch (e) {
        console.error('쿠키 제거 요청 실패:', e)
      }
    }

    // const groobeeScript = document.getElementById('groobee-script')
    // if (groobeeScript) {
    //   groobeeScript.remove()
    // }
    switch (to.path) {
      case '/bp/bpa/SCR_BPA_0008':
      case '/fb/fba/SCR_FBA_0006':
      case '/or/ora/SCR_ORA_0001':
      case '/or/ora/SCR_ORA_0006':
      case '/or/ora/SCR_ORA_0007':
      case '/or/orc/SCR_ORC_0001':
      case '/pd/pdr/SCR_PDR_0001':
      case '/ds/dst/SCR_DST_0015':
        console.log('그루비 기타 페이지 수집 스크립트 예외 처리')
        break
      default: {
        if (to.path.includes('/ds/exhCtgr/')) {
          console.log('그루비 기타 페이지 수집 스크립트 예외 처리')
          break
        }
        // const script = document.createElement('script')
        // script.type = 'text/javascript'
        // script.id = 'groobee-script'
        // script.innerHTML = '(function(){groobee.start();})()'
        // document.head.appendChild(script)
        try {
          // eslint-disable-next-line no-undef
          groobee.start()
        } catch (e) {
          console.log(e)
        }
        break
      }
    }

    // curation 탭 경로 (탭 간 이동 시 스크롤 유지)
    const CURATION_TAB_PATHS = [
      '/ds/curation/interest',
      '/ds/curation/purchase',
      '/ds/curation/total'
    ]

    const isFromCurationTab = CURATION_TAB_PATHS.some(p =>
      from?.path?.includes(p)
    )
    const isToCurationTab = CURATION_TAB_PATHS.some(p =>
      to?.path?.includes(p)
    )
    const isCurationTabSwitch = isFromCurationTab && isToCurationTab

    if (!store.getters['page/getPopstate'] && !isCurationTabSwitch) {
      requestAnimationFrame(() => {
        requestAnimationFrame(() => {
          window.scrollTo(0, 0)
        })
      })
    }
  })
}
